// Realtime duplicate-login detection.
//
// The account is meant to be known by at most two people, so a second login is a security
// event: the backend revokes the older session immediately. Previously the evicted user only
// found out when they next clicked something, and each page reported it differently — a blank
// screen here, nothing at all there. This module gives that one owner:
//
//   1. a response interceptor, so ANY 401 carrying err_code SESSION_REVOKED signs the user out
//      the same way — that covers an active user on their very next request;
//   2. a 10s heartbeat, which covers an IDLE user whose tab is just sitting open;
//   3. a BroadcastChannel, so every tab of the same browser leaves together.
//
// All three funnel into one kickToLogin(), so there is exactly one way a session ends: a single
// dialog, shown over the page the user was already on, that they dismiss when they have read it.
import axios from "axios";

const HEARTBEAT_INTERVAL_MS = 10000;
// The heartbeat and the logout call both need a ceiling: neither has a spinner, and without
// one a hanging backend leaves requests stacking up (heartbeat) or the UI apparently frozen
// (logout) until the browser's own socket timeout fires.
const SESSION_REQUEST_TIMEOUT_MS = 8000;
const CHANNEL_NAME = "ctrlytics-session";
const LOGIN_ROUTE = "/";
const REVOKED_ERR_CODE = "SESSION_REVOKED";
const SUPERSEDED_REASON = "SUPERSEDED";
const MODAL_ID = "ctrlytics-session-ended-modal";
const MODAL_STYLE_ID = "ctrlytics-session-ended-style";

// Ten in-flight requests failing at once must still produce ONE dialog.
let hasKicked = false;
let heartbeatTimer = null;
let channel = null;
// setInterval does not wait for the previous tick, and refocusing the tab fires an extra
// immediate check — without this, a slow backend accumulates overlapping heartbeats.
let isHeartbeatInFlight = false;

const FALLBACK_TEXT = {
  supersededTitle: "Signed out",
  supersededBody:
    "This account was signed in on another device. Only one session is allowed at a time.",
  expiredTitle: "Signed out",
  expiredBody: "Your session is no longer valid. Please sign in again.",
  detailLabel: "From",
  button: "Back to login",
};

function backendUrl(path) {
  return process.env.VUE_APP_BACKEND_URL + path;
}

function getToken() {
  try {
    return localStorage.getItem("token");
  } catch (e) {
    return null;
  }
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function clearCredentials() {
  // Session keys only — the user's language choice and preferences must survive.
  ["token", "bearer_token", "csrf_token", "role", "currentUser"].forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  });
}

function goToLogin() {
  // A full navigation rather than a router push: it guarantees no stale component keeps
  // polling with a dead token, whatever route the user was on.
  window.location.replace(LOGIN_ROUTE);
}

// "127.0.0.1 · 2026-08-19 10:45:40" — the origin of the sign-in that took the account over.
// This is the security payload: it is how the account holder tells a colleague's sign-in from
// an intruder's.
function formatDetail(detail) {
  if (!detail) return "";
  const parts = [];
  if (detail.ip) parts.push(detail.ip);
  if (detail.at) parts.push(detail.at);
  return parts.join(" · ");
}

// Renders in English immediately, then swaps in the user's language if it is available. Doing
// it this way keeps the dialog instant — waiting on a fetch before showing a security notice
// would be the wrong trade.
function localiseModal(parts, isSuperseded, detailText) {
  let langmode = "ENG";
  try {
    langmode = localStorage.getItem("lang") || "ENG";
  } catch (e) {
    // keep the default
  }
  fetch("/login.json")
    .then((response) => response.json())
    .then((data) => {
      const strings = data[langmode];
      if (!strings) return;
      if (isSuperseded) {
        if (strings.session_ended_title) parts.title.textContent = strings.session_ended_title;
        if (strings.session_ended_body) parts.body.textContent = strings.session_ended_body;
      } else {
        if (strings.session_expired_title) parts.title.textContent = strings.session_expired_title;
        if (strings.session_expired_body) parts.body.textContent = strings.session_expired_body;
      }
      if (detailText && strings.session_ended_detail_label) {
        parts.detail.textContent = strings.session_ended_detail_label + " " + detailText;
      }
      if (strings.session_ended_button) parts.button.textContent = strings.session_ended_button;
    })
    .catch(() => {
      // English is already on screen.
    });
}

// Injected once. A stylesheet rather than inline styles because the button needs :hover,
// :active and :focus-visible — inline styles cannot express them, and a primary button with no
// hover feedback reads as disabled. Values mirror the app's own .btn_primary_large so the
// dialog does not look like it came from somewhere else.
function ensureModalStyles() {
  if (document.getElementById(MODAL_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = MODAL_STYLE_ID;
  style.textContent = [
    "@keyframes ctrl-session-fade{from{opacity:0}to{opacity:1}}",
    "@keyframes ctrl-session-rise{from{opacity:0;transform:translateY(12px) scale(.97)}",
    "to{opacity:1;transform:translateY(0) scale(1)}}",
    ".ctrl-session-backdrop{position:fixed;inset:0;z-index:2147483647;display:flex;",
    "align-items:center;justify-content:center;padding:24px;background:rgba(0,0,0,.45);",
    "-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);",
    "animation:ctrl-session-fade .18s ease-out}",
    ".ctrl-session-card{width:100%;max-width:420px;padding:32px 28px;",
    "background:var(--surface-color,#fff);border-radius:20px;",
    "box-shadow:0 18px 40px rgba(0,0,0,.28);text-align:center;font-family:inherit;",
    "color:var(--text-color,#212529);animation:ctrl-session-rise .22s ease-out}",
    ".ctrl-session-title{margin:0 0 10px;font-size:21px;font-weight:700;letter-spacing:-.01em}",
    ".ctrl-session-body{margin:0;font-size:15px;line-height:1.55}",
    ".ctrl-session-detail{margin:16px 0 0;font-size:13px;color:var(--text-muted,#5f7d8b);",
    "word-break:break-word}",
    ".ctrl-session-btn{margin-top:24px;width:100%;padding:13px 20px;border:none;",
    "border-radius:12px;background:var(--primary-color,#ffd600);color:var(--text-main,#212529);",
    "font-size:15px;font-weight:700;font-family:inherit;cursor:pointer;",
    "box-shadow:0 4px 6px rgba(0,0,0,.1);transition:all .2s ease}",
    ".ctrl-session-btn:hover{transform:translateY(-2px);box-shadow:0 6px 12px rgba(0,0,0,.15);",
    "filter:brightness(105%)}",
    ".ctrl-session-btn:active{transform:translateY(0);box-shadow:0 2px 4px rgba(0,0,0,.12);",
    "filter:brightness(97%)}",
    ".ctrl-session-btn:focus-visible{outline:3px solid rgba(0,0,0,.35);outline-offset:2px}",
    "@media (prefers-reduced-motion:reduce){.ctrl-session-backdrop,.ctrl-session-card",
    "{animation:none}.ctrl-session-btn{transition:none}}",
  ].join("");
  document.head.appendChild(style);
}

// Built in plain DOM rather than as a Vue component because it must be able to appear over any
// route, including ones whose own requests have just failed.
function showSessionEndedModal(reason, detail) {
  if (typeof document === "undefined" || document.getElementById(MODAL_ID)) return;
  ensureModalStyles();

  const isSuperseded = reason === SUPERSEDED_REASON;
  // Dimmed and blurred, not blanked: a full white screen reads as a crash rather than a message.
  const backdrop = document.createElement("div");
  backdrop.id = MODAL_ID;
  backdrop.className = "ctrl-session-backdrop";

  const card = document.createElement("div");
  card.setAttribute("role", "alertdialog");
  card.setAttribute("aria-modal", "true");
  card.className = "ctrl-session-card";

  const title = document.createElement("h3");
  title.textContent = isSuperseded ? FALLBACK_TEXT.supersededTitle : FALLBACK_TEXT.expiredTitle;
  title.className = "ctrl-session-title";

  const body = document.createElement("p");
  body.textContent = isSuperseded ? FALLBACK_TEXT.supersededBody : FALLBACK_TEXT.expiredBody;
  body.className = "ctrl-session-body";

  const detailText = isSuperseded ? formatDetail(detail) : "";
  const detailEl = document.createElement("p");
  if (detailText) {
    detailEl.textContent = FALLBACK_TEXT.detailLabel + " " + detailText;
    detailEl.className = "ctrl-session-detail";
  }

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = FALLBACK_TEXT.button;
  button.className = "ctrl-session-btn";
  // No countdown: this is a security notice, so it waits for the person to read it rather than
  // vanishing while they are away from the screen.
  button.addEventListener("click", goToLogin);

  card.appendChild(title);
  card.appendChild(body);
  if (detailText) card.appendChild(detailEl);
  card.appendChild(button);
  backdrop.appendChild(card);
  document.body.appendChild(backdrop);
  button.focus();

  localiseModal({ title: title, body: body, detail: detailEl, button: button },
                isSuperseded, detailText);
}

export function kickToLogin(reason, detail, options) {
  const shouldBroadcast = !options || options.broadcast !== false;
  if (hasKicked) return;
  hasKicked = true;
  stopHeartbeat();
  clearCredentials();
  if (shouldBroadcast && channel) {
    try {
      channel.postMessage({ type: "revoked", reason: reason, detail: detail });
    } catch (e) {
      // ignore
    }
  }
  showSessionEndedModal(reason, detail);
}

function isRevokedResponse(response) {
  return (
    response &&
    response.status === 401 &&
    response.data &&
    response.data.err_code === REVOKED_ERR_CODE
  );
}

async function checkSessionNow() {
  const token = getToken();
  if (!token || hasKicked || isHeartbeatInFlight) return;
  isHeartbeatInFlight = true;
  try {
    await axios.post(
      backendUrl("/v1/session/state"),
      { token: token },
      { timeout: SESSION_REQUEST_TIMEOUT_MS }
    );
  } catch (e) {
    // A revoked session comes back as the 401 envelope and is handled by the interceptor
    // below. Anything else (offline, 5xx, timeout) is ignored on purpose — a network blip
    // must not sign anyone out.
  } finally {
    isHeartbeatInFlight = false;
  }
}

function startHeartbeat() {
  stopHeartbeat();
  if (hasKicked) return;
  heartbeatTimer = setInterval(checkSessionNow, HEARTBEAT_INTERVAL_MS);
}

function handleVisibilityChange() {
  if (document.hidden) {
    // A hidden tab cannot show a dialog, so polling it only burns requests.
    stopHeartbeat();
    return;
  }
  checkSessionNow();
  startHeartbeat();
}

function setupChannel() {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      const data = event && event.data;
      if (data && data.type === "revoked") {
        kickToLogin(data.reason, data.detail, { broadcast: false });
      }
    };
  } catch (e) {
    channel = null;
  }
}

// Ends the session server-side on a deliberate sign-out. Without this the row stays live and
// the user's OWN next login evicts it, raising a security event that never happened.
export function endSession() {
  const token = getToken();
  hasKicked = true; // our own sign-out must not also raise the "signed in elsewhere" dialog
  stopHeartbeat();
  if (!token) return Promise.resolve();
  return axios
    .post(
      backendUrl("/v1/session/logout"),
      { token: token },
      { timeout: SESSION_REQUEST_TIMEOUT_MS }
    )
    .catch(() => {});
}

// Called after a successful login: the app is an SPA, so without this the guard would stay
// disarmed for the rest of the page's life after a sign-out.
export function resetSessionGuard() {
  hasKicked = false;
  isHeartbeatInFlight = false;
  startHeartbeat();
}

export function attachSessionGuard(instance) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isRevokedResponse(error.response)) {
        const data = error.response.data;
        kickToLogin(data.reason, data.detail);
      }
      return Promise.reject(error);
    }
  );
  return instance;
}

// Components import the global axios directly — cover them all, exactly as busyRetry does.
attachSessionGuard(axios);

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", handleVisibilityChange);
  startHeartbeat();
  setupChannel();
}
